import { Coordinate, SpotType } from "~/generated/graphql.types";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import { EditMode } from "../..";

export type SpotForSidebar = {
  coordinate: Coordinate;
  courseId: string;
  description: string | null | undefined;
  id: string;
  assetId: number;
  name: string | null | undefined;
  note: string | null | undefined;
  order: number;
  parentSpotId: string | null | undefined;
  spotOrderLabel: string;
  spotType: SpotType;
  isChild: boolean;
  isRoutingGuide: boolean;
  isCoordinateEditable: boolean;
  isMemoEditable: boolean;
  isNameEditable: boolean;
  isAssetEditable: boolean;
  isRemovable: boolean;
  isSpotTypeEditable: boolean;
  hasLeftDotLine: boolean;
  hasLeftMargin: boolean;
  hasMusic: boolean;
};

export const convertParamsForSidebar = ({
  start,
  dest,
  waypoints,
  editMode,
}: {
  start: SpotForRoutingFragment | undefined;
  dest: SpotForRoutingFragment | undefined;
  waypoints: SpotForRoutingFragment[];
  editMode: EditMode;
}) => {
  const convertStart = start
    ? convertOneSpotToSpotForSidebar({
        spot: start,
        editMode,
        spotOrderLabel: "S",
      })
    : undefined;
  const convertDest = dest
    ? convertOneSpotToSpotForSidebar({
        spot: dest,
        editMode,
        spotOrderLabel: "G",
      })
    : undefined;
  const convertWaypoints = waypoints.map((spot, index) => {
    return convertOneSpotToSpotForSidebar({
      spot,
      editMode,
      spotOrderLabel: waypoints
        .slice(0, index + 1)
        .filter((p) => p?.spotType === spot.spotType)
        .length.toString(),
    });
  });
  const extractWaypoints =
    editMode === "contents"
      ? convertWaypoints
      : convertWaypoints.filter(
          (s) => ![SpotType.Narration].includes(s.spotType)
        );
  return {
    startSidebar: convertStart,
    destSidebar: convertDest,
    waypointsSidebar: extractWaypoints,
  };
};

const convertOneSpotToSpotForSidebar = ({
  spot,
  editMode,
  spotOrderLabel,
}: {
  spot: SpotForRoutingFragment;
  editMode: EditMode;
  spotOrderLabel: string;
}) => {
  const isChild = spot.parentSpotId !== null;
  const isRoutingGuide = spot.spotType == SpotType.RoutingGuide;
  const isCoordinateEditable = () => {
    if (editMode === "routing") {
      return true;
    } else {
      if ([SpotType.Narration].includes(spot.spotType)) {
        return true;
      }
    }
    return false;
  };
  const isMemoEditable = !isRoutingGuide;
  const isNameEditable = !isRoutingGuide;
  const isAssetEditable = !isRoutingGuide;
  const isRemovable = () => {
    if (editMode === "routing") {
      if (
        [
          SpotType.Waypoint,
          SpotType.RoutingGuide,
          ,
          SpotType.SubaruMoment,
        ].includes(spot.spotType)
      ) {
        return true;
      }
    } else {
      if ([SpotType.Narration].includes(spot.spotType)) {
        return true;
      }
    }
    return false;
  };
  const isSpotTypeEditable = () => {
    if (editMode === "routing") {
      if (
        spot.spotType === SpotType.Waypoint ||
        spot.spotType === SpotType.SubaruMoment ||
        spot.spotType === SpotType.RoutingGuide
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };
  const hasLeftDotLine = isRoutingGuide;
  const hasLeftMargin = isChild;
  const spotForSidebar: SpotForSidebar = {
    coordinate: spot.coordinate,
    courseId: spot.courseId,
    description: spot.description,
    id: spot.id,
    assetId: spot.assetId,
    name: spot.name,
    note: spot.note,
    order: spot.order,
    parentSpotId: spot.parentSpotId,
    spotOrderLabel,
    spotType: spot.spotType,
    isChild,
    isRoutingGuide,
    isCoordinateEditable: isCoordinateEditable(),
    isMemoEditable,
    isNameEditable,
    isAssetEditable,
    isRemovable: isRemovable(),
    isSpotTypeEditable: isSpotTypeEditable(),
    hasLeftDotLine,
    hasLeftMargin,
    hasMusic: spot.hasMusic,
  };

  return spotForSidebar;
};
